import { useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { Plus, Search, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useSmartBetaStrategyStore } from '@/features/Strategies/stores/definitionFormStore';
import { FilterResetButton } from '../../components/EligibilityRules/FilterResetButton';

// Keyword-Based Exclusions — renders inside Single Security Exclusions,
// below the Exclusion List. A typed keyword always means "exclude a company if
// the term is FOUND in its description": the backend only supports the
// 'contains' path, so there is no Contains / Doesn't-Contain toggle (it was
// dead UI — the adapter emits only the terms, never a match direction).
//
// The rule terms live in the wizard store (data_processor.keyword_exclusions)
// so they persist across steps and the adapter serializes them into the engine
// config's manual_overrides.keyword_based_exclusions (a plain list[str]).

export function KeywordBasedExclusions() {
  const [searchTerm, setSearchTerm] = useState('');
  const { rules, setKeywordExclusions } = useSmartBetaStrategyStore(
    useShallow((s) => ({
      rules: s.data_processor.keyword_exclusions ?? [],
      setKeywordExclusions: s.setKeywordExclusions,
    })),
  );

  const addRule = () => {
    const trimmed = searchTerm.trim();
    if (!trimmed || rules.includes(trimmed)) {
      setSearchTerm('');
      return;
    }
    setKeywordExclusions([...rules, trimmed]);
    setSearchTerm('');
  };

  const removeRule = (rule: string) => {
    setKeywordExclusions(rules.filter((r) => r !== rule));
  };

  return (
    <div className="space-y-2.5 border-t border-border/10 p-[0px] mx-[0px] my-[10px]">
      {/* Section label + Reset (clears every keyword rule), mirroring the
          Excluded Securities / Security Types headers. */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="font-medium text-muted-foreground text-[15px]">
            Keyword-Based Exclusions
          </span>
          {rules.length > 0 && (
            <FilterResetButton onClick={() => setKeywordExclusions([])} />
          )}
        </div>
      </div>

      <div className="space-y-3.5">
        {/* Input + Add Rule row — figma 5857-5902. */}
        <div className="group">
          <div className="flex items-center gap-2">
            {/* The visible bar (solid bg + border + focus ring) lives on the
                CONTAINER, and the input itself is transparent/borderless. A
                translucent bg ON the input sat over the absolutely-positioned
                icon + placeholder behind it and dimmed them to an illegible
                grey; this keeps the field crisp and the placeholder readable.
                Resting border matches the "Search tickers to exclude" outline
                field (the `border` token); focus keeps the primary ring; h-10
                matches that field. */}
            <div className="relative flex-1 h-10 rounded-md bg-background border transition-colors focus-within:border-ring focus-within:ring-1 focus-within:ring-ring">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 z-10 pointer-events-none">
                <Search className="w-3.5 h-3.5 text-muted-foreground/60 group-focus-within:text-destructive transition-colors" />
              </div>
              {searchTerm === '' && (
                <div className="absolute left-9 top-1/2 -translate-y-1/2 pointer-events-none select-none">
                  <span className="text-muted-foreground text-[14px]">
                    Type terms to{' '}
                    <span className="text-destructive font-medium">exclude</span>{' '}
                    companies if keywords are{' '}
                    <span className="text-destructive font-medium">
                      found
                    </span>{' '}
                    in their descriptions...
                  </span>
                </div>
              )}
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addRule();
                  }
                  e.stopPropagation();
                }}
                className="relative z-10 pl-9 pr-9 h-full bg-transparent border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 w-full font-medium cursor-text"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-muted text-muted-foreground/60 hover:text-destructive transition-all cursor-pointer z-10"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={addRule}
              className="h-9 w-[110px] border-primary/20 bg-primary/[0.06] hover:bg-primary/10 text-primary rounded-[calc(var(--corner-radius)*0.5)] font-medium transition-all shrink-0 flex items-center justify-center gap-2 text-[14px]"
            >
              <Plus className="w-3.5 h-3.5" />
              Add Rule
            </Button>
          </div>
          {/* Hint text — figma 5903. */}
          <div className="mt-1.5 text-[13px] italic text-muted-foreground ml-[15px]">
            e.g. mentions of weapons manufacturing, fossil fuel extraction, or
            quantum computing.
          </div>

          {/* Active rules — an "Excluded Keywords" title above the chips,
              matching the Excluded Sectors / Excluded Security Types groups. */}
          {rules.length > 0 && (
            <div className="mt-4 p-3 rounded-md border border-border bg-muted/30 space-y-1.5">
              <h4 className="text-[12px] font-semibold text-muted-foreground">
                Excluded Keywords
              </h4>
              <div className="flex flex-wrap gap-2">
                {rules.map((rule) => (
                  <Badge
                    key={rule}
                    variant="secondary"
                    className="pl-2 pr-1 py-1 gap-1 bg-destructive/10 text-destructive border-destructive/20 hover:bg-destructive/20"
                  >
                    <span className="font-medium text-[14px]">{rule}</span>
                    <button
                      type="button"
                      onClick={() => removeRule(rule)}
                      className="ml-1 rounded-sm hover:bg-destructive/30 p-0.5"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
